import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import settings from 'settings';
import AuthService from 'library/authService/auth-service';
import _ from 'lodash';

import { connect } from 'react-redux';
import { loadServerAssignments, setTimelineGroups } from 'actions/rosterActions';
import { list } from 'actions';
import REDUCER_KEY from 'reducers/reducer.keys';
import ExtendedSelect from 'components/extendedSelect/extended-select';
import { getKey, getNameLabel } from 'library/helpers';

import {
  combineRosterItems, getPublishedRosters, isLeaveItem, isTravelItem
} from 'selectors/roster.selectors';
import HeaderBar from 'components/headerBar/header-bar';
import ToolBar from 'components/toolBar/tool-bar';
import {
  Icon, Select, Spin, Tooltip
} from 'antd';

import ReactDOM from 'react-dom';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';

import Event from './Event';
import EventAgenda from './EventAgenda';

const getEvent = (item = {}, start) => {
  // Get the event date
  const event = {
    id: item.id,
    title: item.title,
    allDay: false,
    start: item.start_time < start ? start.format('YYYY-MM-DD HH:mm') : item.start_time.format('YYYY-MM-DD HH:mm'),
    end: item.end_time.format('YYYY-MM-DD HH:mm'),
    start_time: item.start_time < start ? start : item.start_time, // keep copy of moment object for timezone
    end_time: item.end_time, // keep copy of moment object for timezone
    color: item.color,
    notes: item.notes,
    multiDay: item.end_time.days() !== item.start_time.days()
  };

  // If the item starts with 't-', then the item is a Travel Activity.
  // Set the title and color of the Travel Activity.
  if (isTravelItem(item)) {
    const title = `Travel: ${item.legs.map(leg => `${leg.route.departure_location.code} > ${leg.route.arrival_location.code}`)} (${item.reference_number})`;
    event.title = title;
    event.color = settings.APP_CONFIG.defaultTravelActivityColor;
  }
  else if (isLeaveItem(item)) {
    const title = `${item.type.name} - ${item.status.status_on_timeline}`;
    event.title = title;
    event.color = item.type.color;
  }

  // Return the event and apply any other attribute overrides as needed
  return event;
};

const mapStateToProps = state => ({
  rosterList: getPublishedRosters(state.api.rosters.data),
  serverItems: state.rosterTimeline.server_items,
  travelItems: state.rosterTimeline.travel_items,
  leaveItems: state.rosterTimeline.leave_items,
  groups: state.rosterTimeline.user_groups,
  userList: state.api.users.data.results,
  usersInFlight: state.api.users.inFlight
});

const mapDispatchToProps = dispatch => ({
  loadRosterList() {
    const page = 0;
    const pageSize = settings.APP_CONFIG.dropListSize;
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [{
      id: 'active',
      value: true
    }];

    return dispatch(list(
      REDUCER_KEY.rosters.key, {
        page,
        pageSize,
        filters,
        sorted
      }
    ));
  },
  loadUserList(searchText = '') {
    const page = 0;
    const pageSize = 20;
    const sorted = [
      {
        id: 'name',
        desc: false,
      },
    ];
    const filters = [
      {
        id: 'active',
        value: true,
      },
    ];
    if (!_.isEmpty(searchText)) {
      filters.push({
        id: 'q',
        value: searchText
      });
    }

    return dispatch(
      list(REDUCER_KEY.users.key, {
        page,
        pageSize,
        filters,
        sorted,
      })
    );
  },
  loadRosterServerAssignments(rosterId, userIds, visibleTimeStart, visibleTimeEnd) {
    const layers = {
      travel: true,
      leave: true,
      availability: false
    };
    return dispatch(loadServerAssignments(rosterId, userIds, visibleTimeStart, visibleTimeEnd, layers, false));
  },
  dispatchSetTimelineGroups(userList) {
    dispatch(setTimelineGroups(userList));
  }
});

class MyRoster extends React.Component {
  constructor(props) {
    super(props);

    const currentUser = AuthService.whoami();
    if (moment.tz.guess() !== currentUser.currentTenancy.timezone) {
      moment.tz.setDefault(currentUser.currentTenancy.timezone);
    }
    const currentDate = moment();
    const visibleTimeStart = moment(currentDate)
      .startOf('month')
      .subtract(7, 'days');
    const visibleTimeEnd = moment(currentDate)
      .endOf('month')
      .add(7, 'days');

    this.state = {
      selectedRosterId: null,
      selectedUser: currentUser,
      currentUser,
      currentDate,
      visibleTimeStart,
      visibleTimeEnd,
      loading: false
    };

    this.handleNavigate = this.handleNavigate.bind(this);
    this.handleRosterChange = this.handleRosterChange.bind(this);
    this.handleUserChange = this.handleUserChange.bind(this);
  }

  componentDidMount() {
    const { loadRosterList } = this.props;

    loadRosterList()
      .then(
        (response) => {
          const rosterList = getPublishedRosters(response);

          // Set the first item as the selected roster
          if (rosterList.length > 0) {
            const { versionId } = rosterList[0];
            this.setState({
              loading: true,
              selectedRosterId: versionId
            }, () => {
              this.loadRosterItems(versionId);
            });
          }
        }
      );
  }

  loadRosterItems(rosterVersionId) {
    const { dispatchSetTimelineGroups, loadRosterServerAssignments } = this.props;
    const { selectedUser, visibleTimeStart, visibleTimeEnd } = this.state;

    const userList = [{
      id: selectedUser.id,
      first_name: selectedUser.firstName,
      last_name: selectedUser.lastName,
      email: selectedUser.userName,
      is_active: true,
      is_ad_user: true
    }];

    // Set the current user in the Timeline Group.
    // Must set this value otherwise the activities
    // will not be retrieve for the user in the timeline
    dispatchSetTimelineGroups(userList);

    // Load the activies for the given users.
    loadRosterServerAssignments(rosterVersionId, [selectedUser.id], visibleTimeStart, visibleTimeEnd)
      .then(() => {
        this.setState({
          loading: false
        });
      },
      () => {
        this.setState({
          loading: false
        });
      });
  }

  refreshRosterItems(start, end) {
    const { selectedRosterId } = this.state;
    // Display the visible Roster Items for the month
    if (selectedRosterId) {
      this.setState({
        visibleTimeStart: moment(start),
        visibleTimeEnd: moment(end)
      }, () => {
        this.loadRosterItems(selectedRosterId);
      });
    }
  }

  handleNavigate({ view }) {
    const { activeStart, activeEnd } = view;
    this.refreshRosterItems(activeStart, activeEnd);
  }

  handleRosterChange(versionId) {
    this.setState({
      loading: true,
      selectedRosterId: versionId
    }, () => {
      this.loadRosterItems(versionId);
    });
  }

  handleUserChange(selectedOption) {
    const { userList } = this.props;
    const { currentUser } = this.state;

    this.setState({
      loading: true,
      selectedUser: selectedOption && userList?.length > 0 ? userList.find(u => u.id === selectedOption.key) : currentUser,
    }, () => {
      const { selectedRosterId, selectedUser } = this.state;
      // Display the visible Roster Items for the month
      if (selectedRosterId && selectedUser) {
        this.loadRosterItems(selectedRosterId);
      }
    });
  }

  renderToolbar() {
    const {
      rosterList, userList, loadUserList, usersInFlight
    } = this.props;
    const {
      selectedRosterId, currentUser
    } = this.state;
    const options = rosterList.map(item => (
      <Select.Option key={item.versionId} value={item.versionId}>
        {item.name}
      </Select.Option>
    ));

    return (
      <ToolBar>
        <ToolBar.Right>
          <div className="bhp-select-grouping">
            <Tooltip className="bhp-select-roster" title="Select Roster to view" placement="top">
              <div className="bhp-tag-arrow">
                Select Roster
              </div>
              <Select placeholder="Select Roster" value={selectedRosterId} onChange={this.handleRosterChange}>
                {options}
              </Select>
            </Tooltip>
            {currentUser.currentTenancy.canViewOthersRoster
              && (
              <Tooltip className="bhp-select-roster" title="Select User to view" placement="top">
                <div className="bhp-tag-arrow">Select User</div>
                <ExtendedSelect
                  placeholder="Myself"
                  optionKey={getKey}
                  optionLabel={getNameLabel}
                  dataSource={userList}
                  dropdownClassName="bhp-select-user-dropdown"
                  fetching={usersInFlight}
                  onSearch={loadUserList}
                  onFocus={loadUserList}
                  onChange={this.handleUserChange}
                />
              </Tooltip>
            )}
          </div>
        </ToolBar.Right>
      </ToolBar>
    );
  }

  render() {
    const {
      groups, serverItems, travelItems, leaveItems
    } = this.props;
    const {
      currentUser, currentDate, loading, visibleTimeStart, visibleTimeEnd
    } = this.state;
    const items = combineRosterItems(groups, serverItems, travelItems, leaveItems);
    const components = {
      dayGridMonth: Event,
      timeGridWeek: Event,
      timeGridDay: Event,
      listWeek: EventAgenda
    };

    return (
      <div className="bhp-container bhp-my-roster">
        <HeaderBar title="My Roster" toolbar={this.renderToolbar()} />
        <Spin indicator={<Icon type="loading" spin />} tip="Loading..." spinning={loading}>
          <FullCalendar
            // The old behaviour was to have different default view which got asked to be removed
            // defaultView={window.innerWidth < 1200 ? 'listWeek' : 'dayGridMonth'}
            defaultView="dayGridMonth"
            now={currentDate.format()}
            defaultDate={currentDate.format()}
            plugins={[dayGridPlugin, listPlugin, timeGridPlugin]}
            events={items.map(event => ({
              ...getEvent(event, visibleTimeStart, visibleTimeEnd)
            }))}
            showMultiDayTimes
            showNonCurrentDates
            fixedWeekCount={false}
            header={{
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
              center: 'title',
              left: 'today, prev,next'
            }}
            eventRender={({ event, el, view }) => {
              if (view.type !== 'listWeek') {
                 const Block = components[view.type];
                // cant spread event
                const e = {
                  title: event.title,
                  id: event.id,
                  allDay: false,
                  color: event.backgroundColor,
                  ...event.extendedProps
                };

                const element = <Block event={e} timezone={currentUser.currentTenancy.timezone} />;

                ReactDOM.render(element, el);
              }

              return el;
            }}
            slotDuration="00:15:00"
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            }}
            buttonText={{
              today: 'Today',
              month: 'Month',
              week: 'Week',
              day: 'Day',
              list: 'Agenda'
            }}
            height="auto"
            contentHeight="auto"
            nowIndicator
            datesRender={this.handleNavigate}
            displayEventEnd
          />
        </Spin>
      </div>
    );
  }
}

MyRoster.propTypes = {
  loadRosterList: PropTypes.func.isRequired,
  loadRosterServerAssignments: PropTypes.func.isRequired,
  dispatchSetTimelineGroups: PropTypes.func.isRequired,
  loadUserList: PropTypes.func.isRequired,
  rosterList: PropTypes.array,
  userList: PropTypes.array,
  usersInFlight: PropTypes.bool,
  groups: PropTypes.array,
  serverItems: PropTypes.array,
  travelItems: PropTypes.array,
  leaveItems: PropTypes.array
};

MyRoster.defaultProps = {
  rosterList: [],
  groups: [],
  userList: [],
  serverItems: [],
  travelItems: [],
  leaveItems: [],
  usersInFlight: false,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MyRoster);
